import React, { useState } from 'react';
import type {
  PlannedVsActualReportResponse,
  PlannedVsActualBoardGroup,
  PlannedVsActualItemRow,
} from '../../api/report.api';
import { formatHours, formatPercent, deviationClass } from '../../utils/planned-vs-actual';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '\u2013';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  data: PlannedVsActualReportResponse;
}

const PlannedVsActualItemTable: React.FC<Props> = ({ data }) => {
  const { boardGroups, grandTotal } = data;
  const gtDev = deviationClass(grandTotal.deviation, grandTotal.originalEstimate, grandTotal.timeSpent);

  return (
    <div className="rp-report-table__wrapper">
      <div className="rp-report-table__scroll">
        <table className="rp-report-table rp-pva-table">
          <thead>
            <tr>
              <th className="rp-pva-table__col-name">Item Name</th>

              <th className="rp-pva-table__col-assignee">Assignee</th>
              <th className="rp-pva-table__col-date">Start</th>
              <th className="rp-pva-table__col-date">End</th>
              <th className="rp-pva-table__col-hours">Original Estimate</th>
              <th className="rp-pva-table__col-hours">Time Spent</th>
              <th className="rp-pva-table__col-hours">Remaining</th>
              <th className="rp-pva-table__col-hours">Deviation</th>
              <th className="rp-pva-table__col-percent">Deviation %</th>
            </tr>
          </thead>
          <tbody>
            {boardGroups.map((group) => (
              <BoardGroup key={group.boardId} group={group} />
            ))}
            {/* Grand total */}
            <tr className="rp-pva-table__summary-row rp-pva-table__grand-total-row">
              <td colSpan={4}><strong>Grand Total</strong></td>
              <td className="rp-pva-table__col-hours">{formatHours(grandTotal.originalEstimate)}</td>
              <td className="rp-pva-table__col-hours">{formatHours(grandTotal.timeSpent)}</td>
              <td className="rp-pva-table__col-hours">{formatHours(grandTotal.remainingEstimate)}</td>
              <td className={`rp-pva-table__col-hours ${gtDev}`}>
                {formatHours(grandTotal.deviation)}
              </td>
              <td className={`rp-pva-table__col-percent ${gtDev}`}>
                {formatPercent(grandTotal.deviationPercent)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Report description */}
      <ReportDescription />
    </div>
  );
};

// ─── Description ─────────────────────────────────────────────────────────────

const ReportDescription: React.FC = () => (
  <div className="rp-report-description">
    <div className="rp-report-description__title">How to read this report</div>
    <p className="rp-report-description__text">
      This report compares <strong>planned hours</strong> (Original Estimate) against
      <strong> actual logged hours</strong> (Time Spent) for each item. Use it to identify
      budget overruns, track remaining effort, and measure estimation accuracy.
    </p>
    <div className="rp-report-description__subtitle">Deviation color coding</div>
    <div className="rp-report-description__legend">
      <span className="rp-report-description__swatch" style={{ background: '#d83a52' }} />
      <span>Over budget &mdash; Time Spent exceeds the Original Estimate</span>
    </div>
    <div className="rp-report-description__legend">
      <span className="rp-report-description__swatch" style={{ background: '#00a25b' }} />
      <span>On track &mdash; Time Spent is within 20% of the Original Estimate</span>
    </div>
    <div className="rp-report-description__legend">
      <span className="rp-report-description__swatch" style={{ background: '#fdab3d' }} />
      <span>Under-logged &mdash; significant gap between estimate and actual hours (less than 80% logged)</span>
    </div>
    <div className="rp-report-description__legend">
      <span className="rp-report-description__swatch" style={{ background: '#676879' }} />
      <span>No data &mdash; neither estimate nor time spent recorded</span>
    </div>
    <div className="rp-report-description__subtitle">Key details</div>
    <ul className="rp-report-description__list">
      <li>
        <strong>Original Estimate</strong> is sourced from the mapped hours column. If a
        &ldquo;hours per day&rdquo; column is mapped instead, the total is calculated as
        hours/day &times; working days in the timeline.
      </li>
      <li>
        <strong>Time Spent</strong> reflects all worklogs ever logged for the item (not limited to the selected period).
      </li>
      <li>
        <strong>Remaining Estimate</strong> = Original Estimate &minus; Time Spent.
        Shows how much work is left. If all work is logged, it shows 0.
      </li>
      <li>
        <strong>Deviation</strong> = Time Spent &minus; Original Estimate. Positive means over budget.
      </li>
      <li>
        Items prefixed with <strong>&darr;</strong> are subitems. In the &ldquo;By Board&rdquo; view,
        subitems are aggregated into their parent board&rsquo;s totals.
      </li>
    </ul>
  </div>
);

// ─── Board group ─────────────────────────────────────────────────────────────

const BoardGroup: React.FC<{ group: PlannedVsActualBoardGroup }> = ({ group }) => {
  const [collapsed, setCollapsed] = useState(false);
  const sDev = deviationClass(group.summary.deviation, group.summary.originalEstimate, group.summary.timeSpent);

  return (
    <>
      <tr
        className="rp-report-table__group-row rp-pva-table__board-header"
        onClick={() => setCollapsed(!collapsed)}
        style={{ cursor: 'pointer' }}
      >
        <td colSpan={9}>
          <strong>{collapsed ? '\u25B6' : '\u25BC'} {group.boardName}</strong>
          <span style={{ marginLeft: 8, fontWeight: 'normal', color: '#676879' }}>
            ({group.items.length} items)
          </span>
        </td>
      </tr>
      {!collapsed && group.items.map((item) => (
        <ItemRow key={`${item.boardId}-${item.itemId}-${item.isSubitem}`} item={item} />
      ))}
      {!collapsed && (
        <tr className="rp-pva-table__summary-row">
          <td colSpan={4}>
            <strong>Summary for {group.boardName}</strong>
          </td>
          <td className="rp-pva-table__col-hours">{formatHours(group.summary.originalEstimate)}</td>
          <td className="rp-pva-table__col-hours">{formatHours(group.summary.timeSpent)}</td>
          <td className="rp-pva-table__col-hours">{formatHours(group.summary.remainingEstimate)}</td>
          <td className={`rp-pva-table__col-hours ${sDev}`}>
            {formatHours(group.summary.deviation)}
          </td>
          <td className={`rp-pva-table__col-percent ${sDev}`}>
            {formatPercent(group.summary.deviationPercent)}
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Item row ────────────────────────────────────────────────────────────────

const ItemRow: React.FC<{ item: PlannedVsActualItemRow }> = ({ item }) => {
  const dev = deviationClass(item.deviation, item.originalEstimate, item.timeSpent);
  return (
    <tr className={`rp-report-table__user-row${item.isSubitem ? ' rp-pva-table__subitem-row' : ''}`}>
      <td className="rp-pva-table__col-name">
        {item.isSubitem ? '\u21B3 ' : ''}{item.itemName}
      </td>
      <td className="rp-pva-table__col-assignee">{item.assignees || '\u2013'}</td>
      <td className="rp-pva-table__col-date">{formatDate(item.startDate)}</td>
      <td className="rp-pva-table__col-date">{formatDate(item.endDate)}</td>
      <td className="rp-pva-table__col-hours">{formatHours(item.originalEstimate)}</td>
      <td className="rp-pva-table__col-hours">{formatHours(item.timeSpent)}</td>
      <td className="rp-pva-table__col-hours">{formatHours(item.remainingEstimate)}</td>
      <td className={`rp-pva-table__col-hours ${dev}`}>
        {formatHours(item.deviation)}
      </td>
      <td className={`rp-pva-table__col-percent ${dev}`}>
        {formatPercent(item.deviationPercent)}
      </td>
    </tr>
  );
};

export default PlannedVsActualItemTable;
